import { useState, useEffect } from 'react';
import ICAL from 'ical.js';
import { apiFetch } from '../api';

function AirbnbReservations({ bookingData, onDataChange, refreshKey }) {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [editingField, setEditingField] = useState(null); // { id, field: 'amount' | 'name' }
  const [editValue, setEditValue] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parseResult, setParseResult] = useState(null);
  const [localRefresh, setLocalRefresh] = useState(0);

  useEffect(() => {
    async function fetchReservations() {
      try {
        const res = await apiFetch('/api/calendar/airbnb');
        const text = await res.text();
        const parsed = ICAL.parse(text);
        const comp = new ICAL.Component(parsed);
        const vevents = comp.getAllSubcomponents('vevent');

        const now = new Date();
        now.setHours(0, 0, 0, 0);

        const upcoming = vevents
          .map((ve) => {
            const event = new ICAL.Event(ve);
            const summary = event.summary || '';
            const description = ve.getFirstPropertyValue('description') || '';
            const phone = extractPhone(description);

            return {
              id: event.uid || Math.random().toString(36),
              summary,
              icalGuest: extractGuest(summary, description),
              phone,
              checkIn: event.startDate.toJSDate(),
              checkOut: event.endDate.toJSDate(),
              description,
              nights: Math.round(
                (event.endDate.toJSDate() - event.startDate.toJSDate()) /
                  (1000 * 60 * 60 * 24)
              ),
            };
          })
          .filter((r) => r.checkOut >= now && r.summary !== 'Airbnb (Not available)')
          .sort((a, b) => a.checkIn - b.checkIn);

        setReservations(upcoming);
        setError(null);
      } catch (err) {
        setError('Could not load Airbnb reservations');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    fetchReservations();
  }, [refreshKey, localRefresh]);

  async function deleteReservation(e, resId) {
    e.stopPropagation();
    if (!window.confirm('Remove this reservation from the app? It will stay removed even if it reappears in the Airbnb iCal.')) return;
    try {
      await apiFetch(`/api/calendar/airbnb/${encodeURIComponent(resId)}`, { method: 'DELETE' });
      setLocalRefresh((k) => k + 1);
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  }

  function extractGuest(summary, description) {
    if (summary && summary !== 'Reserved' && summary !== 'Airbnb (Not available)') {
      return summary;
    }
    const match = description.match(/(?:Guest|Reservation for):?\s*(.+)/i);
    if (match) return match[1].trim();
    return summary || 'Guest';
  }

  function extractPhone(description) {
    const match = description.match(/(?:Phone|Tel|Contact):?\s*([\d\s+()-]+)/i);
    if (match) return match[1].trim();
    return '';
  }

  function getDisplayName(res) {
    const custom = bookingData[res.id]?.name;
    return custom || res.icalGuest;
  }

  function getStatus(res) {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const checkIn = new Date(res.checkIn);
    checkIn.setHours(0, 0, 0, 0);
    const checkOut = new Date(res.checkOut);
    checkOut.setHours(0, 0, 0, 0);

    if (now >= checkIn && now < checkOut) return 'current';
    if (checkIn.getTime() === now.getTime()) return 'today';

    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (checkIn.getTime() === tomorrow.getTime()) return 'tomorrow';

    return 'upcoming';
  }

  function formatDate(date) {
    return date.toLocaleDateString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric' });
  }

  function startEditing(e, resId, field) {
    e.stopPropagation();
    setEditingField({ id: resId, field });
    if (field === 'amount') {
      const amount = bookingData[resId]?.amount;
      setEditValue(amount ? String(amount) : '');
    } else {
      setEditValue(bookingData[resId]?.name || '');
    }
  }

  async function saveField(e, resId) {
    e.stopPropagation();
    const field = editingField?.field;
    const body = {};
    if (field === 'amount') {
      body.amount = parseFloat(editValue) || 0;
    } else {
      body.name = editValue.trim();
    }

    try {
      await apiFetch(`/api/bookings/${encodeURIComponent(resId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      onDataChange();
    } catch (err) {
      console.error('Failed to save:', err);
    }
    setEditingField(null);
  }

  function handleKeyDown(e, resId) {
    if (e.key === 'Enter') saveField(e, resId);
    if (e.key === 'Escape') setEditingField(null);
  }

  async function parseAirbnbEmails() {
    setParsing(true);
    setParseResult(null);
    try {
      const res = await apiFetch('/api/bookings/parse-airbnb-emails', { method: 'POST' });
      const data = await res.json();
      setParseResult(data);
      if (data.updated > 0) {
        onDataChange();
      }
    } catch (err) {
      console.error(err);
      setParseResult({ errors: 1 });
    } finally {
      setParsing(false);
    }
  }

  if (loading) {
    return (
      <section className="card">
        <h2>Airbnb Reservations</h2>
        <p className="loading">Loading reservations...</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="card">
        <h2>Airbnb Reservations</h2>
        <p className="error">{error}</p>
      </section>
    );
  }

  return (
    <section className="card airbnb-section">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Airbnb Reservations</h2>
        <button
          className="receipt-action-btn import-btn"
          onClick={parseAirbnbEmails}
          disabled={parsing}
          style={{ fontSize: 12 }}
        >
          {parsing ? 'Parsing...' : 'Parse Emails'}
        </button>
      </div>
      {parseResult && (
        <div className={`sfin-import-result ${parseResult.errors ? 'sfin-import-error' : 'sfin-import-success'}`} style={{ marginBottom: 8 }}>
          <span>
            {parseResult.errors
              ? 'Parse failed'
              : `Found ${parseResult.emailsFound} emails, updated ${parseResult.updated} reservations`}
          </span>
          <button className="modal-close" onClick={() => setParseResult(null)} style={{ marginLeft: 8, background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 16 }}>&times;</button>
        </div>
      )}
      {reservations.length === 0 ? (
        <p className="empty">No upcoming reservations</p>
      ) : (
        <ul className="reservation-list">
          {reservations.map((res) => {
            const status = getStatus(res);
            const info = bookingData[res.id] || {};
            const isExpanded = expandedId === res.id;
            const isEditingName = editingField?.id === res.id && editingField?.field === 'name';
            const isEditingAmount = editingField?.id === res.id && editingField?.field === 'amount';

            return (
              <li
                key={res.id}
                className={`reservation-item status-${status}`}
                onClick={() => setExpandedId(isExpanded ? null : res.id)}
              >
                <div className="res-info">
                  <span className="res-guest">{getDisplayName(res)}</span>
                  <span className="res-dates">
                    {formatDate(res.checkIn)} - {formatDate(res.checkOut)}
                    <span className="res-nights"> ({res.nights} night{res.nights !== 1 ? 's' : ''})</span>
                  </span>
                </div>
                <div className="res-right">
                  {info.amount > 0 && !isEditingAmount && (
                    <span
                      className="res-amount"
                      onClick={(e) => startEditing(e, res.id, 'amount')}
                    >
                      ${Number(info.amount).toLocaleString()}
                    </span>
                  )}
                  {status === 'current' && <span className="res-badge current-badge">Staying now</span>}
                  {status === 'today' && <span className="res-badge today-badge">Check-in today</span>}
                  {status === 'tomorrow' && <span className="res-badge tomorrow-badge">Tomorrow</span>}
                </div>
                {isExpanded && (
                  <div className="res-details" onClick={(e) => e.stopPropagation()}>
                    <div className="res-edit-row">
                      <span className="res-edit-label">Guest name:</span>
                      {isEditingName ? (
                        <div className="res-edit-inline">
                          <input
                            type="text"
                            className="res-edit-input"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, res.id)}
                            autoFocus
                            placeholder={res.icalGuest}
                          />
                          <button className="res-save-btn" onClick={(e) => saveField(e, res.id)}>Save</button>
                          <button className="res-cancel-btn" onClick={(e) => { e.stopPropagation(); setEditingField(null); }}>Cancel</button>
                        </div>
                      ) : (
                        <button
                          className="res-set-amount-btn"
                          onClick={(e) => startEditing(e, res.id, 'name')}
                        >
                          {info.name || res.icalGuest} (edit)
                        </button>
                      )}
                    </div>
                    <div className="res-edit-row">
                      <span className="res-edit-label">Booking amount:</span>
                      {isEditingAmount ? (
                        <div className="res-edit-inline">
                          <span className="res-dollar">$</span>
                          <input
                            type="number"
                            className="res-edit-input res-amount-input"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, res.id)}
                            autoFocus
                            min="0"
                            step="0.01"
                          />
                          <button className="res-save-btn" onClick={(e) => saveField(e, res.id)}>Save</button>
                          <button className="res-cancel-btn" onClick={(e) => { e.stopPropagation(); setEditingField(null); }}>Cancel</button>
                        </div>
                      ) : (
                        <button
                          className="res-set-amount-btn"
                          onClick={(e) => startEditing(e, res.id, 'amount')}
                        >
                          {info.amount > 0 ? `$${Number(info.amount).toLocaleString()} (edit)` : 'Set amount'}
                        </button>
                      )}
                    </div>
                    {res.description && (
                      <pre className="res-description">{res.description}</pre>
                    )}
                    {res.phone && <div className="res-phone">Phone: {res.phone}</div>}
                    <div className="res-edit-row" style={{ marginTop: 8 }}>
                      <button
                        className="res-cancel-btn"
                        onClick={(e) => deleteReservation(e, res.id)}
                        style={{ background: '#7f1d1d', color: '#fecaca', borderColor: '#991b1b' }}
                      >
                        Remove reservation
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export default AirbnbReservations;
